import moment from 'moment';
import { getFormattedPessoaCnpj } from '@/utils';
import { ITINERARY_STATUS } from '../constants/itineraryStatus';

const DATE_FORMAT = 'DD/MM/YYYY';
const TIME_FORMAT = 'HH:mm';

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export function formatCnpj(cnpj) {
  const digits = String(cnpj || '').replace(/\D/g, '');
  if (digits.length !== 14) return cnpj || '';
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

export function mapStopFromApi(pj) {
  return {
    id: pj.pessoaId,
    pessoaId: pj.pessoaId,
    nome: pj.name,
    cnpj: pj.cnpj,
    cidade: pj.city,
    uf: pj.uf,
    itineraryPessoaJuridicaId: pj.itineraryPessoaJuridicaId,
  };
}

export function mapItineraryFromApi(raw) {
  const createdAtMoment = raw.createdAt ? moment(raw.createdAt, 'YYYY-MM-DD HH:mm:ss') : null;
  const updatedAtMoment = raw.updatedAt ? moment(raw.updatedAt, 'YYYY-MM-DD HH:mm:ss') : null;
  const dateMoment = updatedAtMoment || createdAtMoment;
  const stops = (raw.pessoasJuridicas || []).map(mapStopFromApi);
  const lastEditor = raw.updatedBy && raw.updatedBy.name ? raw.updatedBy.name : null;
  const creator = raw.createdBy && raw.createdBy.name ? raw.createdBy.name : null;

  return {
    id: raw.cd,
    itineraryId: raw.itineraryId,
    nome: raw.descr,
    descricao: raw.obs || '',
    stops: stops,
    paradas: stops.map(function (stop) {
      return { nome: stop.nome || '', cnpj: formatCnpj(stop.cnpj) };
    }),
    status: raw.status && raw.status.descr,
    statusValor: raw.status ? Number(raw.status.valor) : null,
    isDraft: raw.isDraft,
    usuario: lastEditor || creator,
    role: null,
    data: dateMoment ? dateMoment.format(DATE_FORMAT) : null,
    hora: dateMoment ? dateMoment.format(TIME_FORMAT) : null,
  };
}

export function mapDraftFromApi(raw) {
  return {
    id: raw.itineraryId,
    codigo: raw.cd,
    nome: raw.descr || '',
    descricao: raw.obs || '',
    paradas: [],
    status: 'draft',
    step: 0,
  };
}

export function mapPessoaFromLookup(raw) {
  return {
    id: raw.pessoaId,
    pessoaId: raw.pessoaId,
    nome: raw.name,
    cnpj: raw.cnpj,
    cidade: raw.city,
    uf: raw.uf,
  };
}

export function mapPessoasFromLookup(rawList) {
  return (rawList || []).map(mapPessoaFromLookup);
}

export function mapSupplierOptionsFromApi(data) {
  if (!data || typeof data !== 'object') {
    return [];
  }
  return (data.items || [])
    .map(function (raw) {
      return {
        value: Number(raw.pessoa_id),
        label: raw.razao_social || '',
        documento: getFormattedPessoaCnpj({ cnpj: raw.cnpj }),
      };
    })
    .filter(function (option) {
      return option.value > 0;
    });
}

export function mapAddedPessoaIds(rawList) {
  const map = {};
  (rawList || []).forEach(function (item) {
    map[item.pessoaId] = item.itineraryPessoaJuridicaId;
  });
  return map;
}

function historyInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '--';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function historyStatusLabel(value) {
  return Number(value) === ITINERARY_STATUS.ATIVO ? 'Ativo' : 'Inativo';
}

export function buildHistoryDescription(raw) {
  const oldV = raw.oldValues || {};
  const newV = raw.newValues || {};

  switch (raw.event) {
    case 'updated': {
      const parts = [];
      if ((oldV.descr || '') !== (newV.descr || '')) {
        parts.push('Nome alterado para «' + (newV.descr || '') + '»');
      }
      if ((oldV.obs || '') !== (newV.obs || '')) {
        parts.push('Observação atualizada');
      }
      return parts.length ? parts.join('; ') : 'Detalhes atualizados';
    }
    case 'stop_added': {
      const added = newV.added || [];
      const names = added.map((item) => item.name).filter(Boolean);
      if (names.length === 1) return 'Parada «' + names[0] + '» adicionada';
      if (names.length > 1) {
        return 'Paradas adicionadas: ' + names.map((n) => '«' + n + '»').join(', ');
      }
      return added.length === 1
        ? '1 parada adicionada'
        : added.length + ' paradas adicionadas';
    }
    case 'stop_removed': {
      const removed = oldV.removed || [];
      const names = removed.map((item) => item.name).filter(Boolean);
      if (names.length === 1) return 'Parada «' + names[0] + '» removida';
      if (names.length > 1) {
        return 'Paradas removidas: ' + names.map((n) => '«' + n + '»').join(', ');
      }
      return removed.length === 1
        ? '1 parada removida'
        : removed.length + ' paradas removidas';
    }
    case 'finalized':
      return 'Roteiro finalizado';
    case 'imported':
      return newV.filename
        ? 'Inclusão via Upload em massa — arquivo «' + newV.filename + '»'
        : 'Inclusão via Upload em massa';
    case 'deleted':
      return 'Roteiro excluído';
    case 'status_changed':
      return 'Status alterado para ' + historyStatusLabel(newV.status);
    default:
      return 'Alteração registrada';
  }
}

export function mapHistoryEntryFromApi(raw) {
  const m = raw.changedAt ? moment(raw.changedAt, 'YYYY-MM-DD HH:mm:ss') : null;
  const name = raw.changedBy && raw.changedBy.name ? raw.changedBy.name : 'Sistema';

  return {
    id: raw.itineraryAuditId,
    event: raw.event,
    description: buildHistoryDescription(raw),
    userName: name,
    initials: historyInitials(name),
    dateKey: m ? m.format('YYYY-MM-DD') : '',
    dayLabel: m ? m.date() + ' de ' + MONTHS_PT[m.month()] : '',
    yearLabel: m ? String(m.year()) : '',
    timestamp: m ? m.format('DD/MM/YY, HH:mm') : '',
  };
}

export function mapHistoryFromApi(rawList) {
  return (rawList || []).map(mapHistoryEntryFromApi);
}
